import { Categories } from '@/enums'
import { getYearlyPriceFromGrossPrice } from '@/modules/price/price'
import { Insurance } from '@/types/insurance'
import { HomeRecommendation } from '@/types/location'
import { formatMoney } from '@/utils'
import { groupBy, prop } from 'ramda'

export const chartColors: string[] = [
  '#010B65',
  '#0317CE',
  '#7987FF',
  '#A9AFE1',
  '#CFD4FF',
  '#818499',
]

const emptyChartColor = '#E4EAF1'

export interface ChartData {
  id: string
  name: string
  label: string
  value: number
  color: string
  recommendationId?: string
}

const sortCategoryIdsByEssentialOrder = (
  allCategoryIds: string[],
  essentialCategoryIds: string[],
): string[] => {
  const nonEssentialCategoryIds = allCategoryIds.filter(
    (id) => !essentialCategoryIds.includes(id),
  )
  return [
    ...essentialCategoryIds.filter((id) => allCategoryIds.includes(id)),
    ...nonEssentialCategoryIds,
  ]
}

interface ChartDataInput {
  categoryId: string
  price: number
  type: 'insurance' | 'recommendation'
  recommendationId?: string
}

const getPricesForExistingInsuranceCategories = (
  allInsurances: Insurance[],
  essentialCategoryIds: string[] = [],
): ChartDataInput[] => {
  const groupedByCategory = groupBy(prop('categoryId'), allInsurances)

  const categoriesWithPrices = Object.fromEntries(
    Object.entries(groupedByCategory).map(([categoryId, insurances = []]) => {
      return [
        categoryId,
        insurances.reduce(
          (sum, insurance) =>
            sum +
            getYearlyPriceFromGrossPrice(
              insurance.grossPrice || 0,
              insurance.paymentPeriod || 'yearly',
            ),
          0,
        ),
      ]
    }),
  )

  const allCategoryIds = Object.keys(categoriesWithPrices)
  const sortedCategoryIds = sortCategoryIdsByEssentialOrder(
    allCategoryIds,
    essentialCategoryIds,
  )

  const existingInsurancePrices: ChartDataInput[] = sortedCategoryIds.map(
    (categoryId) => ({
      categoryId,
      price: categoriesWithPrices[categoryId],
      type: 'insurance',
    }),
  )

  return existingInsurancePrices
}

const getPricesForLatestEssentialRecommendations = (
  recommendations: HomeRecommendation[],
  existingCategoryIds: string[],
  essentialCategoryIds: string[],
): ChartDataInput[] => {
  const sentRecommendations = recommendations.filter((r) => r.sended)

  const sortedRecommendationsByMostRecent = sentRecommendations.sort((a, b) => {
    const dateA = new Date(a.updatedAt || 0)
    const dateB = new Date(b.updatedAt || 0)
    return dateB.getTime() - dateA.getTime()
  })

  const recommendationsByCategory = groupBy(
    prop('categoryId'),
    sortedRecommendationsByMostRecent,
  )

  const categoryIdsMissingFromExistingInsurance = Object.keys(
    recommendationsByCategory,
  ).filter((id) => !existingCategoryIds.includes(id))

  const sortedCategoryIds = sortCategoryIdsByEssentialOrder(
    categoryIdsMissingFromExistingInsurance,
    essentialCategoryIds,
  )

  return sortedCategoryIds.map((categoryId) => {
    const firstRecommendation =
      recommendationsByCategory?.[
        categoryId as keyof typeof recommendationsByCategory
      ]?.[0]

    return {
      categoryId,
      recommendationId: firstRecommendation?.recommendationId,
      price: getYearlyPriceFromGrossPrice(
        firstRecommendation?.lowestPrice || 0,
        firstRecommendation?.searchCriteria?.paymentPeriod || 'yearly',
      ),
      type: 'recommendation',
    }
  })
}

const truncateExistingInsurancesWithOther = (
  items: ChartDataInput[],
): ChartDataInput[] => {
  const firstThree = items.slice(0, 3)
  const other = items.slice(3)

  const otherTotal = other.reduce((sum, item) => sum + item.price, 0)

  const otherEntry: ChartDataInput = {
    categoryId: 'other',
    price: otherTotal,
    type: 'insurance',
  }

  const entries = [...firstThree]

  if (items.length > 3) {
    entries.push(otherEntry)
  }

  return entries
}

const mapToChartData = (
  items: ChartDataInput[],
  isMissingCoverage: boolean,
  t: (key: string) => string,
): ChartData[] => {
  return items.map((item, idx) => {
    const category = Categories[item.categoryId as keyof typeof Categories]
    const categoryName =
      item.categoryId === 'other'
        ? t('common.other')
        : t(`categories.${category?.key}.name`)

    return {
      id: `${item.type}-${item.categoryId}`,
      name: categoryName,
      label: `${categoryName} ${formatMoney(item.price)}`,
      value: item.price,
      color: isMissingCoverage
        ? emptyChartColor
        : chartColors[idx % chartColors.length],
      recommendationId: item.recommendationId,
    }
  })
}

export interface ChartDataOutput {
  chartData: ChartData[]
  grossPrice: number
  existingInsuranceData: ChartData[]
  latestEssentialRecommendationsData: ChartData[]
}

export const prepareChartData = (
  recommendations: HomeRecommendation[],
  insurances: Insurance[],
  essentialCategoryIds: string[],
  t: (key: string) => string,
): ChartDataOutput => {
  const existingInsurancePrices = getPricesForExistingInsuranceCategories(
    insurances,
    essentialCategoryIds,
  )

  const existingInsurancePricesWithOther = truncateExistingInsurancesWithOther(
    existingInsurancePrices,
  )

  const existingCategoryIds = existingInsurancePrices.map(
    (item) => item.categoryId,
  )

  const mappedExistingInsurancePrices = mapToChartData(
    existingInsurancePricesWithOther,
    false,
    t,
  )

  const latestEssentialRecommendations = getPricesForLatestEssentialRecommendations(
    recommendations,
    existingCategoryIds,
    essentialCategoryIds,
  )

  const mappedLatestEssentialRecommendations = mapToChartData(
    latestEssentialRecommendations,
    true,
    t,
  )

  const chartData: ChartData[] = [
    ...mappedExistingInsurancePrices,
    ...mappedLatestEssentialRecommendations.slice(0, 3),
  ]

  return {
    chartData,
    grossPrice: insurances.reduce(
      (sum, insurance) =>
        sum +
        getYearlyPriceFromGrossPrice(
          insurance.grossPrice || 0,
          insurance.paymentPeriod || 'yearly',
        ),
      0,
    ),
    existingInsuranceData: mappedExistingInsurancePrices,
    latestEssentialRecommendationsData: mappedLatestEssentialRecommendations,
  }
}

interface InternalChartText {
  integer: string
  decimal: string
}

export const getInternalChartText = (grossPrice: number): InternalChartText => {
  const integerPart = Math.floor(grossPrice)
  const decimalPart = Number(grossPrice - integerPart)
    .toFixed(2)
    .split('.')[1]

  return {
    integer: formatMoney(integerPart, 'euro', {
      style: 'decimal',
    }),
    decimal: decimalPart,
  }
}
